import React, { useState, useEffect } from "react";
import {
    ReloadOutlined,
    DollarOutlined,
    UserOutlined,
    MailOutlined,
    PhoneOutlined,
    CalendarOutlined,
    EyeOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    LockOutlined,
    ExclamationCircleOutlined
} from "@ant-design/icons";
import { Tag, Modal, Button, Col, Space, Select, Typography, Switch, message, Row, Avatar, Tooltip, Input } from "antd";
import Breadcrumbs from "../../component/Breadcrumbs";
import PayoutServ from "../../service/PayoutServ";
import TableActions from "../../component/ui/TableActions";
import { FilterBar } from "../../component/ui/FilterBar";
import SearchInput from "../../component/ui/SearchInput";
import { ResetButton } from "../../component/ui/ActionButtons";
import DataTable from "../../component/ui/DataTable";
import DateRangePicker from "../../component/ui/DateRangePicker";
import QuickFilter from "../../component/ui/QuickFilter";
import { usePreference } from "../../context/PreferenceContext";
import dayjs from "dayjs";
import { useAdminTable } from "../hooks/useAdminTable";
import Notify from "../../util/Notify";

const { Text, Title } = Typography;
const { Option } = Select;

// Premium Warning Pulse Payout Status Switching Badge
const PayoutStatusBadge = ({ isPaid, loading, onChange }) => {
    const bg = isPaid ? "#f0fdf4" : "#fff5f5";
    const border = isPaid ? "1px solid #bbf7d0" : "1.5px solid #feb2b2";
    const color = isPaid ? "#16a34a" : "#dc2626";
    const pulseClass = !isPaid ? "payout-pulse-warning" : "";

    return (
        <div
            className={pulseClass}
            style={{
                background: bg,
                border: border,
                borderRadius: "12px",
                padding: "4px 10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "8px",
                minWidth: "125px",
                margin: "0 auto",
                boxShadow: isPaid ? "0 0 8px rgba(34, 197, 94, 0.15)" : "none",
                transition: "all 0.3s ease"
            }}
            onClick={e => e.stopPropagation()}
        >
            <span style={{
                fontSize: "10px",
                fontWeight: 800,
                color: color,
                textTransform: "uppercase",
                display: "flex",
                alignItems: "center",
                gap: "5px"
            }}>
                <span style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: color,
                    display: "inline-block"
                }} />
                {isPaid ? "Paid Out" : "Pending"}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {isPaid && (
                    <Tooltip title="Paid payouts require confirmation before reverting">
                        <LockOutlined style={{ color: '#fbbf24', fontSize: '12px' }} />
                    </Tooltip>
                )}
                <Switch
                    size="small"
                    checked={isPaid}
                    loading={loading}
                    onChange={onChange}
                    style={{
                        backgroundColor: isPaid ? '#16a34a' : '#ef4444'
                    }}
                />
            </div>
        </div>
    );
};

const formatAddress = (addr) => {
    if (!addr) return "";
    if (typeof addr === 'string') return addr;
    if (addr.address) return addr.address;

    const parts = [];
    if (addr.name) parts.push(addr.name);
    if (addr.street) parts.push(addr.street);
    if (addr.taluk) parts.push(addr.taluk);
    if (addr.city) parts.push(addr.city);
    if (addr.district) parts.push(addr.district);
    if (addr.pincode || addr.zipCode) parts.push(addr.pincode || addr.zipCode);
    if (addr.phone) parts.push(`(Phone: ${addr.phone})`);

    return parts.join(', ');
};

const DeliveryPayoutHistory = () => {
    const { formatPrice } = usePreference();

    const [deliveries, setDeliveries] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState(""); // Default: All
    const [paymentMethodFilter, setPaymentMethodFilter] = useState("");
    const [monthFilter, setMonthFilter] = useState("");
    const [yearFilter, setYearFilter] = useState("");
    const [dateRange, setDateRange] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalDeliveries, setTotalDeliveries] = useState(0);
    const [quickFilter, setQuickFilter] = useState(undefined);

    const [selectedRecord, setSelectedRecord] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [toggleLoading, setToggleLoading] = useState({});

    // Reversal Modal State
    const [revertModalVisible, setRevertModalVisible] = useState(false);
    const [revertRecord, setRevertRecord] = useState(null);
    const [revertReason, setRevertReason] = useState("");
    const [revertingLoading, setRevertingLoading] = useState(false);

    const fetchPayouts = React.useCallback(async (p = page, size = pageSize, sText = searchText, statFilter = statusFilter, payMethod = paymentMethodFilter, drange = dateRange) => {
        try {
            setLoading(true);
            const res = await PayoutServ.getDeliveryPayouts({
                page: p,
                limit: size,
                ...(payMethod && { paymentMethod: payMethod }),
                ...(drange && drange[0] && { startDate: drange[0].startOf('day').toISOString() }),
                ...(drange && drange[1] && { endDate: drange[1].endOf('day').toISOString() }),
                ...(sText && { search: sText }),
                ...(statFilter && { payoutStatus: statFilter })
            });
            const dataList = Array.isArray(res) ? res : (res && Array.isArray(res.data) ? res.data : []);

            setDeliveries(dataList);
            if (res?.stats) setStats(res.stats);
            setTotalDeliveries(res?.total || dataList.length);
        } catch (error) {
            Notify.error("Failed to fetch payouts list");
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, searchText, statusFilter, paymentMethodFilter, dateRange]);

    useEffect(() => {
        fetchPayouts(page, pageSize, searchText, statusFilter, paymentMethodFilter, dateRange);
    }, [fetchPayouts, page, pageSize, searchText, statusFilter, paymentMethodFilter, dateRange]);

    // ─── Optimistic payout toggle ─────────────────────────────────────────────
    const handleTogglePayout = async (record, checked) => {
        const deliveryId = record._id;

        // Payout Reversal Protection
        if (!checked && record.deliveryPayoutStatus === "PAID_OUT") {
            setRevertRecord(record);
            setRevertReason("");
            setRevertModalVisible(true);
            return;
        }

        const newStatus = checked ? "PAID_OUT" : "NOT_PAID";
        await processPayoutUpdate(deliveryId, newStatus, record);
    };

    const processPayoutUpdate = async (deliveryId, newStatus, record, reason = null) => {
        try {
            setToggleLoading(prev => ({ ...prev, [deliveryId]: true }));

            const updated = await PayoutServ.updateDeliveryPayoutStatus(deliveryId, newStatus, reason);

            // Merge updated record into master list
            const updatedRecord = updated && updated._id ? updated : {
                ...record,
                deliveryPayoutStatus: newStatus,
                deliveryPayoutDate: newStatus === "PAID_OUT" ? new Date().toISOString() : null,
            };

            const shouldRemove = (statusFilter === "NOT_PAID" && newStatus === "PAID_OUT") ||
                (statusFilter === "PAID_OUT" && newStatus === "NOT_PAID");

            if (shouldRemove) {
                setDeliveries(prev => prev.filter(d => d._id !== deliveryId));
            } else {
                setDeliveries(prev => prev.map(d => d._id === deliveryId ? updatedRecord : d));
            }

            if (selectedRecord && selectedRecord._id === deliveryId) {
                setSelectedRecord(updatedRecord);
            }

            Notify.success(`Delivery marked as ${newStatus === "PAID_OUT" ? "Paid Out" : "Pending"}`);
        } catch (error) {
            Notify.error(error?.message || "Failed to update payout status");
            return false;
        } finally {
            setToggleLoading(prev => ({ ...prev, [deliveryId]: false }));
        }
    };

    const confirmReversal = async () => {
        if (!revertReason.trim()) {
            Notify.error("Please provide a reason for reversing the payout.");
            return;
        }

        setRevertingLoading(true);
        const success = await processPayoutUpdate(revertRecord._id, "NOT_PAID", revertRecord, revertReason);
        setRevertingLoading(false);

        if (success) {
            setRevertModalVisible(false);
            setRevertRecord(null);
        }
    };

    const handleRowClick = async (record) => {
        try {
            setLoading(true);
            const res = await PayoutServ.getDeliveryPayoutDetails(record._id);
            setSelectedRecord(res);
            setIsModalOpen(true);
        } catch (error) {
            Notify.error("Failed to load payout details");
        } finally {
            setLoading(false);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedRecord(null);
    };

    const columns = [
        {
            title: "Order ID",
            key: "order",
            render: (_, record) => (
                <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>
                    #{record.orderId?.orderId || "—"}
                </span>
            )
        },
        {
            title: "Delivery Staff Name",
            key: "staff",
            render: (_, record) => {
                const staff = record.assignedStaffId;
                const name = staff ? `${staff.firstName || ''} ${staff.lastName || ''}`.trim() : 'Unassigned';
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#f0f2f5', color: '#4776E6' }} src={staff?.imageUrl} />
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontWeight: 500, color: '#1e293b', fontSize: '13px' }}>
                                {name}
                            </span>
                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                                {staff?.phone || 'No Phone'}
                            </span>
                        </div>
                    </div>
                );
            }
        },
        {
            title: "Distance",
            key: "distance",
            render: (_, record) => {
                let dist = record.assignmentHistory?.find(a => a.distanceKm)?.distanceKm || record.assignmentHistory?.[0]?.distanceKm || record.distanceKm || record.distance || null;

                // Fallback: Calculate distance from coordinates if missing
                if (!dist && record.pickupLocation?.coordinates?.length === 2 && record.deliveryLocation?.coordinates?.length === 2) {
                    const [lon1, lat1] = record.pickupLocation.coordinates;
                    const [lon2, lat2] = record.deliveryLocation.coordinates;
                    if (lat1 && lon1 && lat2 && lon2) {
                        const p = 0.017453292519943295;
                        const c = Math.cos;
                        const a = 0.5 - c((lat2 - lat1) * p) / 2 + c(lat1 * p) * c(lat2 * p) * (1 - c((lon2 - lon1) * p)) / 2;
                        dist = 12742 * Math.asin(Math.sqrt(a));
                    }
                }

                return (
                    <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>
                        {dist ? `${Number(dist).toFixed(1)} km` : "—"}
                    </span>
                );
            }
        },
        {
            title: "Delivered Date",
            key: "deliveredDate",
            render: (_, record) => (
                <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>
                    {record.actualDeliveryTime ? dayjs(record.actualDeliveryTime).format('DD MMM YYYY') : "—"}
                </span>
            )
        },
        {
            title: "Delivery Earnings",
            key: "earnings",
            align: "right",
            render: (_, record) => {
                const deliveryCharge = record.orderId?.deliveryCharge || 0;
                const extraCharge = record.orderId?.extraDeliveryCharge || 0;
                const totalEarning = deliveryCharge + extraCharge;
                return (
                    <span style={{ color: '#10b981', fontWeight: 600, fontSize: '13px' }}>
                        {formatPrice(totalEarning)}
                    </span>
                );
            }
        },
        {
            title: "Payout Status",
            key: "payoutStatus",
            width: 145,
            align: 'center',
            render: (_, record) => {
                const isPaid = record.deliveryPayoutStatus === "PAID_OUT";
                return (
                    <PayoutStatusBadge
                        isPaid={isPaid}
                        loading={toggleLoading[record._id]}
                        onChange={(checked) => handleTogglePayout(record, checked)}
                    />
                );
            }
        },
        {
            title: "Payout Date",
            key: "payoutDate",
            render: (_, record) => (
                <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>
                    {record.deliveryPayoutDate ? dayjs(record.deliveryPayoutDate).format('DD MMM YYYY, hh:mm A') : "—"}
                </span>
            )
        },
        {
            title: "Payment Method",
            key: "paymentMethod",
            render: (_, record) => {
                const { formatPaymentDisplay } = require('../../util/paymentFormatter');
                const method = formatPaymentDisplay(
                    record.orderId?.paymentMethod || record.orderId?.paymentType || record.orderId?.deliveryPaymentMethod || "COD",
                    record.orderId?.paymentCollectionMethod || record.orderId?.paymentMethod || record.orderId?.paymentType,
                    record.orderId?.paymentSubMethod
                );
                return (
                    <Tag color={method.startsWith('COD') && !method.includes('ONLINE') ? 'default' : 'cyan'} style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' }}>
                        {method.toUpperCase()}
                    </Tag>
                );
            }
        },
        {
            title: "Transaction ID",
            key: "transactionId",
            render: (_, record) => (
                <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>
                    {record.orderId?.transactionId || "-"}
                </span>
            )
        },
        {
            title: "Action",
            key: "action",
            align: "center",
            width: 100,
            render: (_, record) => (
                <Button
                    type="primary"
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(record);
                    }}
                    style={{
                        borderRadius: 3,
                        fontWeight: 600,
                        background: '#ffffffff',
                        color: '#004754ff',
                        border: '1px solid #e4e4e7',
                        boxShadow: '0 0 0 1px #e4e4e7',
                        height: 'auto',
                        padding: '0px 8px',
                        fontSize: 13,
                    }}
                >
                    View
                </Button>
            )
        }
    ];

    const fetchAllPayouts = async () => {
        try {
            const res = await PayoutServ.getDeliveryPayouts({
                page: 1,
                limit: 200,
                ...(paymentMethodFilter && { paymentMethod: paymentMethodFilter }),
                ...(dateRange && dateRange[0] && { startDate: dateRange[0].startOf('day').toISOString() }),
                ...(dateRange && dateRange[1] && { endDate: dateRange[1].endOf('day').toISOString() }),
                ...(searchText && { search: searchText }),
                ...(statusFilter && { payoutStatus: statusFilter })
            });
            return Array.isArray(res) ? res : (res && Array.isArray(res.data) ? res.data : []);
        } catch (error) {
            console.error("Error fetching all payouts for export:", error);
            return [];
        }
    };

    return (
        <div className="pcu-padding-24">
            {/* Inject warning pulse stylesheet dynamically */}
            <style>{`
                @keyframes pulse-warning-glow {
                    0% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4); }
                    70% { box-shadow: 0 0 0 6px rgba(220, 38, 38, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0); }
                }
                .payout-pulse-warning {
                    animation: pulse-warning-glow 2s infinite;
                }
                .payout-stat-card {
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
                    cursor: pointer;
                    user-select: none;
                }
                .payout-stat-card:hover {
                    transform: translateY(-3px) scale(1.02) !important;
                    box-shadow: 0 8px 24px rgba(0,0,0,0.15) !important;
                }
                .payout-stat-card:active {
                    transform: translateY(-1px) scale(1) !important;
                }
                .payout-stat-card.active-card:hover {
                    transform: scale(1.025) !important;
                }
            `}</style>

            <div className="pcu-margin-bottom-16">
                <Breadcrumbs items={[{ title: "Admin" }, { title: "Delivery Staff Payout History" }]} />
            </div>

            <div className="sd-container" style={{ padding: '24px', background: 'white', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                {/* Header Section */}
                <div className="sd-header" style={{ marginBottom: '24px', background: 'transparent', padding: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <div className="sd-orders-icon-wrap" style={{ width: '48px', height: '48px', background: '#fff', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid #f1f5f9' }}>
                            <DollarOutlined style={{ fontSize: '22px', color: '#4776E6' }} />
                        </div>
                        <div>
                            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#1a1d2e', letterSpacing: '-0.5px' }}>Delivery Staff Payout History</h1>
                            <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af', fontWeight: 500 }}>{totalDeliveries} completed transactions in delivery payout ledger</p>
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <TableActions
                            onFetchFullData={fetchAllPayouts}
                            columns={columns}
                            fileName="delivery_staff_payout_history"
                            pageName="Delivery Staff Payout History"
                        />
                        <ResetButton
                            icon={<ReloadOutlined />}
                            onClick={() => {
                                setSearchText("");
                                setStatusFilter(""); // Reset to All
                                setPaymentMethodFilter(""); // Reset payment method filter
                                setYearFilter("");
                                setMonthFilter("");
                                setDateRange(null);
                                setQuickFilter(undefined);
                                fetchPayouts();
                            }}
                            text="Refresh"
                        />
                    </div>
                </div>

                {/* PREMIUM SUMMARY STATS SECTION - EXACTLY MATCHING IMAGE 2 UI STYLE */}
                <Row gutter={[16, 16]} className="pcu-margin-bottom-24" style={{ marginBottom: '24px' }}>
                    {/* Card 1: Total Payouts */}
                    <Col xs={24} md={8}>
                        <div
                            className={`payout-stat-card ${statusFilter === "" ? "active-card" : ""}`}
                            onClick={() => { setStatusFilter(""); setPage(1); }}
                            style={{
                                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                padding: '16px 20px',
                                borderRadius: '16px',
                                color: '#fff',
                                position: 'relative',
                                overflow: 'hidden',
                                boxShadow: statusFilter === "" ? '0 8px 24px rgba(37, 99, 235, 0.45)' : '0 4px 14px rgba(37, 99, 235, 0.25)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '16px',
                                height: '92px',
                                border: statusFilter === "" ? '2px solid rgba(255, 255, 255, 0.85)' : '2px solid transparent',
                                transform: statusFilter === "" ? 'scale(1.025)' : 'scale(1)',
                            }}
                        >
                            <div style={{ position: 'absolute', right: -5, bottom: -10, opacity: 0.12 }}>
                                <DollarOutlined style={{ fontSize: '76px', color: '#fff' }} />
                            </div>
                            <div style={{
                                width: '44px',
                                height: '44px',
                                background: 'rgba(255, 255, 255, 0.22)',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <DollarOutlined style={{ fontSize: '20px', color: '#fff' }} />
                            </div>
                            <div style={{ position: 'relative', zIndex: 1 }}>
                                <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.9, lineHeight: 1.2 }}>
                                    Total Payouts
                                </div>
                                <div style={{ fontSize: '24px', fontWeight: 800, marginTop: '4px', lineHeight: 1.1 }}>
                                    {stats?.totalRecords || 0}
                                </div>
                            </div>
                        </div>
                    </Col>

                    {/* Card 2: Total Pending */}
                    <Col xs={24} md={8}>
                        <div
                            className={`payout-stat-card ${statusFilter === "NOT_PAID" ? "active-card" : ""}`}
                            onClick={() => { setStatusFilter("NOT_PAID"); setPage(1); }}
                            style={{
                                background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                                padding: '16px 20px',
                                borderRadius: '16px',
                                color: '#fff',
                                position: 'relative',
                                overflow: 'hidden',
                                boxShadow: statusFilter === "NOT_PAID" ? '0 8px 24px rgba(234, 88, 12, 0.45)' : '0 4px 14px rgba(234, 88, 12, 0.25)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '16px',
                                height: '92px',
                                border: statusFilter === "NOT_PAID" ? '2px solid rgba(255, 255, 255, 0.85)' : '2px solid transparent',
                                transform: statusFilter === "NOT_PAID" ? 'scale(1.025)' : 'scale(1)',
                            }}
                        >
                            <div style={{ position: 'absolute', right: -5, bottom: -10, opacity: 0.12 }}>
                                <ClockCircleOutlined style={{ fontSize: '76px', color: '#fff' }} />
                            </div>
                            <div style={{
                                width: '44px',
                                height: '44px',
                                background: 'rgba(255, 255, 255, 0.22)',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <ClockCircleOutlined style={{ fontSize: '20px', color: '#fff' }} />
                            </div>
                            <div style={{ position: 'relative', zIndex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.9, lineHeight: 1.2 }}>
                                        Total Pending
                                    </span>
                                    <span style={{
                                        background: 'rgba(255, 255, 255, 0.25)',
                                        padding: '1px 5px',
                                        borderRadius: '4px',
                                        fontSize: '9px',
                                        fontWeight: 700
                                    }}>
                                        {stats?.pendingPayoutCount || 0} Pending
                                    </span>
                                </div>
                                <div style={{ fontSize: '22px', fontWeight: 800, marginTop: '4px', lineHeight: 1.1 }}>
                                    {formatPrice(stats?.totalNotPaidAmount || 0)}
                                </div>
                            </div>
                        </div>
                    </Col>

                    {/* Card 3: Total Paid */}
                    <Col xs={24} md={8}>
                        <div
                            className={`payout-stat-card ${statusFilter === "PAID_OUT" ? "active-card" : ""}`}
                            onClick={() => setStatusFilter("PAID_OUT")}
                            style={{
                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                padding: '16px 20px',
                                borderRadius: '16px',
                                color: '#fff',
                                position: 'relative',
                                overflow: 'hidden',
                                boxShadow: statusFilter === "PAID_OUT" ? '0 8px 24px rgba(5, 150, 105, 0.45)' : '0 4px 14px rgba(5, 150, 105, 0.25)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '16px',
                                height: '92px',
                                border: statusFilter === "PAID_OUT" ? '2px solid rgba(255, 255, 255, 0.85)' : '2px solid transparent',
                                transform: statusFilter === "PAID_OUT" ? 'scale(1.025)' : 'scale(1)',
                            }}
                        >
                            <div style={{ position: 'absolute', right: -5, bottom: -10, opacity: 0.12 }}>
                                <CheckCircleOutlined style={{ fontSize: '76px', color: '#fff' }} />
                            </div>
                            <div style={{
                                width: '44px',
                                height: '44px',
                                background: 'rgba(255, 255, 255, 0.22)',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <CheckCircleOutlined style={{ fontSize: '20px', color: '#fff' }} />
                            </div>
                            <div style={{ position: 'relative', zIndex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.9, lineHeight: 1.2 }}>
                                        Total Paid
                                    </span>
                                    <span style={{
                                        background: 'rgba(255, 255, 255, 0.25)',
                                        padding: '1px 5px',
                                        borderRadius: '4px',
                                        fontSize: '9px',
                                        fontWeight: 700
                                    }}>
                                        {stats?.totalPaidOutCount || 0} Paid
                                    </span>
                                </div>
                                <div style={{ fontSize: '22px', fontWeight: 800, marginTop: '4px', lineHeight: 1.1 }}>
                                    {formatPrice(stats?.totalPaidOutAmount || 0)}
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* Filters & Table View */}
                <div className="pcu-padding-0">
                    <FilterBar>
                        {/* 1. Search */}
                        <Col xs={24} md={6}>
                            <SearchInput
                                label="Search"
                                placeholder="Search by Delivery ID, Order ID or Staff..."
                                value={searchText}
                                onChange={e => setSearchText(e.target.value)}
                            />
                        </Col>

                        {/* 2. Payout Status (Only Pending & Paid Out) */}
                        <Col xs={24} md={6}>
                            <div>
                                <div className="pcu-font-11 pcu-font-700 pcu-margin-bottom-6 pcu-color-title pcu-opacity-50 pcu-letter-spacing-1 pcu-uppercase" style={{ fontFamily: 'var(--font-family-primary)' }}>
                                    Transfer Status
                                </div>
                                <Select
                                    value={statusFilter}
                                    onChange={setStatusFilter}
                                    style={{ width: "100%", height: "34px" }}
                                    className="custom-select"
                                >
                                    <Option value="">All Statuses</Option>
                                    <Option value="NOT_PAID">Pending</Option>
                                    <Option value="PAID_OUT">Transferred</Option>
                                </Select>
                            </div>
                        </Col>

                        {/* 3. Payment Method */}
                        <Col xs={24} md={6}>
                            <div>
                                <div className="pcu-font-11 pcu-font-700 pcu-margin-bottom-6 pcu-color-title pcu-opacity-50 pcu-letter-spacing-1 pcu-uppercase" style={{ fontFamily: 'var(--font-family-primary)' }}>
                                    Payment Method
                                </div>
                                <Select
                                    value={paymentMethodFilter}
                                    onChange={setPaymentMethodFilter}
                                    style={{ width: "100%", height: "34px" }}
                                    className="custom-select"
                                >
                                    <Option value="">All Methods</Option>
                                    <Option value="COD">COD</Option>
                                    <Option value="ONLINE">ONLINE</Option>
                                    <Option value="COD_ONLINE">COD + ONLINE</Option>
                                </Select>
                            </div>
                        </Col>

                        {/* 4. Quick Filter */}
                        <Col xs={24} md={6}>
                            <QuickFilter
                                value={quickFilter}
                                onChange={(key, range) => {
                                    setQuickFilter(key);
                                    setDateRange(range);
                                }}
                            />
                        </Col>
                    </FilterBar>

                    <div className="pcu-border-top" style={{ marginTop: '20px' }}>
                        <DataTable
                            columns={columns}
                            data={deliveries}
                            rowKey="_id"
                            loading={loading}
                            onRow={(record) => ({
                                style: { cursor: 'default' }
                            })}
                            pagination={{
                                current: page,
                                pageSize: pageSize,
                                total: totalDeliveries,
                                onShowSizeChange: (c, s) => { setPageSize(s); setPage(1); },
                                onChange: (p) => {
                                    setPage(p);
                                    const pageContent = document.querySelector('.page-content');
                                    if (pageContent) {
                                        pageContent.scrollTo({ top: 0, behavior: 'smooth' });
                                    } else {
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }
                                },
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* View Details Modal */}
            <Modal
                open={isModalOpen}
                onCancel={handleCloseModal}
                footer={null}
                width={540}
                closable={true}
                centered
                maskStyle={{ backdropFilter: "blur(4px)" }}
                style={{ borderRadius: 16, overflow: "hidden" }}
                bodyStyle={{ padding: 0 }}
            >
                {selectedRecord && (
                    <div style={{ background: "#fff", borderRadius: 16, overflow: "hidden" }}>
                        {/* Modal Header */}
                        <div style={{
                            padding: "16px 20px 12px",
                            borderBottom: "1px solid #e2e8f0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between"
                        }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                <div style={{
                                    width: 44,
                                    height: 44,
                                    border: "1px solid #e2e8f0",
                                    borderRadius: 8,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background: "#f8fafc",
                                    padding: 2,
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.02)"
                                }}>
                                    <Avatar
                                        size={34}
                                        icon={<UserOutlined />}
                                        src={selectedRecord.assignedStaffId?.imageUrl}
                                        style={{
                                            backgroundColor: '#f1f5f9',
                                            color: '#4776E6',
                                            borderRadius: 6
                                        }}
                                    />
                                </div>
                                <div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <span style={{ fontSize: 14, fontWeight: 500, color: "#1e293b" }}>
                                            {selectedRecord.assignedStaffId ? `${selectedRecord.assignedStaffId.firstName} ${selectedRecord.assignedStaffId.lastName}` : "Unknown Staff"}
                                        </span>
                                        <span style={{
                                            padding: '1px 5px',
                                            borderRadius: '3px',
                                            fontSize: '9px',
                                            fontWeight: 600,
                                            backgroundColor: selectedRecord.deliveryPayoutStatus === 'PAID_OUT' ? '#e6fbf1' : '#fff9e6',
                                            color: selectedRecord.deliveryPayoutStatus === 'PAID_OUT' ? '#10b981' : '#f59e0b',
                                            textTransform: 'uppercase',
                                            border: `1px solid ${selectedRecord.deliveryPayoutStatus === 'PAID_OUT' ? '#a7f3d0' : '#fef08a'}`
                                        }}>
                                            {selectedRecord.deliveryPayoutStatus === 'PAID_OUT' ? 'Paid Out' : 'Pending'}
                                        </span>
                                        {selectedRecord.payoutReversalHistory?.length > 0 && (
                                            <Tooltip title="This payout was previously reverted">
                                                <span style={{
                                                    padding: '1px 5px',
                                                    borderRadius: '3px',
                                                    fontSize: '9px',
                                                    fontWeight: 600,
                                                    backgroundColor: '#fef2f2',
                                                    color: '#ef4444',
                                                    textTransform: 'uppercase',
                                                    border: '1px solid #fca5a5',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}>
                                                    <ExclamationCircleOutlined /> Reverted Past
                                                </span>
                                            </Tooltip>
                                        )}
                                    </div>
                                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                                        ID: {selectedRecord.assignedStaffId?.userId || selectedRecord.assignedStaffId?._id?.substring(0, 10).toUpperCase()} • {selectedRecord.type || "Normal"} Delivery
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Modal Body */}
                        <div style={{ padding: "16px 20px", maxHeight: "60vh", overflowY: "auto" }}>
                            {/* Logistics & Staff Details */}
                            <div style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                                Logistics & Staff Details
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
                                <div style={{ background: "#fff", padding: "8px 12px", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                                    <div style={{ fontSize: 9, fontWeight: 500, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Staff Phone</div>
                                    <div style={{ fontSize: 12, fontWeight: 500, color: "#1e293b" }}>{selectedRecord.assignedStaffId?.phone || "No Phone"}</div>
                                </div>

                                <div style={{ background: "#fff", padding: "8px 12px", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                                    <div style={{ fontSize: 9, fontWeight: 500, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Staff Email</div>
                                    <div style={{ fontSize: 12, fontWeight: 500, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selectedRecord.assignedStaffId?.email || "No Email"}</div>
                                </div>

                                <div style={{ background: "#fff", padding: "8px 12px", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                                    <div style={{ fontSize: 9, fontWeight: 500, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Delivery Reference</div>
                                    <div style={{ fontSize: 12, fontWeight: 500, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selectedRecord.id || "Manual"}</div>
                                </div>

                                <div style={{ background: "#fff", padding: "8px 12px", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                                    <div style={{ fontSize: 9, fontWeight: 500, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Order Reference</div>
                                    <div style={{ fontSize: 12, fontWeight: 500, color: "#4776E6" }}>#{selectedRecord.orderId?.orderId || "—"}</div>
                                </div>

                                <div style={{ background: "#fff", padding: "8px 12px", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                                    <div style={{ fontSize: 9, fontWeight: 500, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Delivery Date & Time</div>
                                    <div style={{ fontSize: 12, fontWeight: 500, color: "#1e293b" }}>
                                        {selectedRecord.actualDeliveryTime ? dayjs(selectedRecord.actualDeliveryTime).format('DD MMM YYYY, hh:mm A') : "—"}
                                    </div>
                                </div>

                                <div style={{ background: "#fff", padding: "8px 12px", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                                    <div style={{ fontSize: 9, fontWeight: 500, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Payout Timestamp</div>
                                    <div style={{ fontSize: 12, fontWeight: 500, color: "#1e293b" }}>
                                        {selectedRecord.deliveryPayoutDate ? dayjs(selectedRecord.deliveryPayoutDate).format('DD MMM YYYY, hh:mm A') : "—"}
                                    </div>
                                </div>
                            </div>

                            {/* Route Summary */}
                            <div style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                                Route Summary
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8, marginBottom: 16 }}>
                                <div style={{ background: "#f8fafc", padding: "8px 12px", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                                    <div style={{ fontSize: 9, fontWeight: 500, color: "#4776E6", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Pickup Location Address</div>
                                    <div style={{ fontSize: 11, fontWeight: 500, color: "#334155" }}>
                                        {formatAddress(selectedRecord.pickupAddress) || "Shop Address Location Not Configured"}
                                    </div>
                                </div>

                                <div style={{ background: "#f8fafc", padding: "8px 12px", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                                    <div style={{ fontSize: 9, fontWeight: 500, color: "#e11d48", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Customer Delivery Address</div>
                                    <div style={{ fontSize: 11, fontWeight: 500, color: "#334155" }}>
                                        {formatAddress(selectedRecord.deliveryAddress) || "Customer Address Location Not Configured"}
                                    </div>
                                </div>
                            </div>


                            {/* Earnings Breakdown */}
                            <div style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                                Earnings Breakdown
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                                <div style={{ background: "#fff", padding: "8px 12px", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                                    <div style={{ fontSize: 9, fontWeight: 500, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Standard Delivery Charge</div>
                                    <div style={{ fontSize: 14, fontWeight: 500, color: "#1e293b" }}>
                                        {formatPrice(selectedRecord.orderId?.deliveryCharge || 0)}
                                    </div>
                                </div>

                                <div style={{ background: "#fff", padding: "8px 12px", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                                    <div style={{ fontSize: 9, fontWeight: 500, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Surcharges & Extra Distance</div>
                                    <div style={{ fontSize: 14, fontWeight: 500, color: "#1e293b" }}>
                                        {formatPrice(selectedRecord.orderId?.extraDeliveryCharge || 0)}
                                    </div>
                                </div>
                            </div>

                            <div style={{
                                background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
                                padding: "10px 14px",
                                borderRadius: 8,
                                border: "1px solid #bbf7d0",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: 16
                            }}>
                                <div>
                                    <span style={{ fontSize: 9, fontWeight: 600, color: "#16a34a", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                        Total Delivery Staff Earning
                                    </span>
                                </div>
                                <div style={{ fontSize: 16, color: "#15803d", fontWeight: 700 }}>
                                    {formatPrice((selectedRecord.orderId?.deliveryCharge || 0) + (selectedRecord.orderId?.extraDeliveryCharge || 0))}
                                </div>
                            </div>

                            {/* Payout History Logs */}
                            <div style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                                Payout History logs
                            </div>
                            <div style={{ background: "#fff", padding: "8px 12px", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                                {(!selectedRecord.history || selectedRecord.history.length === 0) ? (
                                    <div style={{ fontSize: 11, color: "#94a3b8" }}>No history logs recorded.</div>
                                ) : (
                                    selectedRecord.history.map((log, idx) => (
                                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: idx !== (selectedRecord.history.length - 1) ? 8 : 0 }}>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span style={{ fontSize: '11px', fontWeight: 500, color: '#1e293b' }}>
                                                    {log.comment || `Status updated to ${log.status}`}
                                                </span>
                                                <span style={{ fontSize: '9px', color: '#94a3b8' }}>
                                                    By: {log.updatedBy ? "Administrator" : "System"}
                                                </span>
                                            </div>
                                            <span style={{ fontSize: '10px', color: '#94a3b8' }}>
                                                {dayjs(log.timestamp).format("DD MMM, hh:mm A")}
                                            </span>
                                        </div>
                                    ))
                                )}
                            </div>

                            {/* Audit History Section */}
                            {(selectedRecord.payoutReversalHistory && selectedRecord.payoutReversalHistory.length > 0) && (
                                <div style={{ marginTop: 16 }}>
                                    <div style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                                        Payout Audit History
                                    </div>
                                    <div style={{
                                        background: '#fff',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '12px',
                                        padding: '12px 16px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '12px',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                                    }}>
                                        {selectedRecord.payoutReversalHistory.map((history, idx) => (
                                            <div key={idx} style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                paddingBottom: idx !== selectedRecord.payoutReversalHistory.length - 1 ? '12px' : '0',
                                                borderBottom: idx !== selectedRecord.payoutReversalHistory.length - 1 ? '1px solid #f1f5f9' : 'none'
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                                    <ClockCircleOutlined style={{ fontSize: '12px', color: '#64748b' }} />
                                                    <span style={{ fontSize: 12, fontWeight: 500, color: '#1e293b' }}>
                                                        {dayjs(history.changedAt).format('DD MMM YYYY, hh:mm A')}
                                                    </span>
                                                    <span style={{ fontSize: 11, color: '#64748b' }}>
                                                        - Reverted to PENDING by Admin
                                                    </span>
                                                </div>
                                                <div style={{
                                                    fontSize: 12,
                                                    color: '#ef4444',
                                                    background: '#fef2f2',
                                                    padding: '6px 10px',
                                                    borderRadius: '6px',
                                                    border: '1px solid #fee2e2',
                                                    marginTop: '4px'
                                                }}>
                                                    <strong>Reason:</strong> {history.reason}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div style={{
                            padding: '12px 20px',
                            background: '#f8fafc',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderTop: "1px solid #e2e8f0"
                        }}>
                            <div>
                                {selectedRecord.deliveryPayoutStatus === 'PAID_OUT' && (
                                    <Button
                                        danger
                                        icon={<ExclamationCircleOutlined />}
                                        onClick={() => {
                                            setRevertRecord(selectedRecord);
                                            setRevertReason("");
                                            setRevertModalVisible(true);
                                        }}
                                        style={{
                                            borderRadius: 6,
                                            fontWeight: 600,
                                            height: 32,
                                            fontSize: '12px',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                    >
                                        Revert to Pending
                                    </Button>
                                )}
                            </div>
                            <Button
                                onClick={handleCloseModal}
                                style={{
                                    borderRadius: 6,
                                    fontWeight: 500,
                                    height: 32,
                                    padding: '0 16px',
                                    border: '1px solid #d1d5db',
                                    fontSize: '12px',
                                    background: '#fff',
                                    color: '#374151',
                                    boxShadow: "0 1px 1px rgba(0,0,0,0.05)"
                                }}
                            >
                                Close
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Reversal Confirmation Modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#dc2626' }}>
                        <ExclamationCircleOutlined style={{ fontSize: '20px' }} />
                        <span>Reverse Delivery Staff Payout?</span>
                    </div>
                }
                open={revertModalVisible}
                onCancel={() => !revertingLoading && setRevertModalVisible(false)}
                footer={[
                    <Button key="cancel" onClick={() => setRevertModalVisible(false)} disabled={revertingLoading}>
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        danger
                        loading={revertingLoading}
                        onClick={confirmReversal}
                    >
                        Inform Staff & Revert
                    </Button>
                ]}
                maskClosable={false}
                closable={!revertingLoading}
            >
                <div style={{ marginBottom: '20px' }}>
                    <p style={{ fontSize: '14px', color: '#4b5563', marginBottom: '8px' }}>
                        You are about to revert a completed payout (Delivery ID: <strong>{revertRecord?.id || revertRecord?._id?.substring(0, 8)}</strong>) back to pending.
                    </p>
                    <p style={{ fontSize: '14px', color: '#4b5563', marginBottom: '16px' }}>
                        This action should only be used if the payout was marked as paid by mistake.
                        <strong> The delivery staff will be notified about this payout reversal.</strong>
                    </p>

                    <div style={{ marginBottom: '8px' }}>
                        <Text strong>Reason for Reversal <span style={{ color: '#ef4444' }}>*</span></Text>
                    </div>
                    <Input.TextArea
                        rows={4}
                        placeholder="e.g., Bank transfer failed, Manual mistake, Duplicate payout..."
                        value={revertReason}
                        onChange={(e) => setRevertReason(e.target.value)}
                        disabled={revertingLoading}
                    />
                </div>
            </Modal>
        </div>
    );
};

export default DeliveryPayoutHistory;
